<template>
  <div class="geo-radius-selector">
    <div class="geo-radius-search" v-if="!readonly">
      <el-input
        v-model="searchQuery"
        placeholder="Search by address, zip, metro area or lat/long"
        clearable
        size="medium"
        @keyup.enter.native="handleSearch"
        @clear="clearSearch">
        <template slot="prefix">
          <i class="el-icon-search"></i>
        </template>
        <template slot="append">
          <el-button
            type="primary"
            plain
            @click="handleSearch"
            :loading="searching"
            :disabled="searching">
            <i class="el-icon-search"></i>
          </el-button>
        </template>
      </el-input>
    </div>
    <div ref="map" class="geo-radius-map"></div>
    <div class="geo-radius-controls">
      <div class="d-flex align-items-center justify-content-between">
        <label class="mb-0 font-weight-600 text-dark">Radius (drag the circle edge)</label>
        <span class="geo-radius-meta">Lat: {{ latDisplay }}, Lng: {{ lngDisplay }}, Radius: {{ radiusMilesDisplay }} mi</span>
      </div>
      <small class="geo-radius-meta">Tap anywhere on the map to move the center. Drag the circle to reposition or resize.</small>
    </div>
  </div>
</template>

<script>
import { Notification, Button } from 'element-ui';

export default {
  name: 'GeoRadiusSelector',
  components: {
    [Button.name]: Button,
  },
  props: {
    lat: {
      type: Number,
      default: null
    },
    lng: {
      type: Number,
      default: null
    },
    radiusMiles: {
      type: Number,
      default: null
    },
    defaultCenter: {
      type: Array,
      default: () => [37.0902, -95.7129] // USA
    },
    defaultRadius: {
      type: Number,
      default: 25
    },
    readonly: {
      type: Boolean,
      default: false
    },
    locationName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      map: null,
      circle: null,
      drawnItems: null,
      leafletReady: false,
      radiusMilesInternal: null,
      latInternal: null,
      lngInternal: null,
      maxRadiusMiles: 100,
      suppressEmit: false,
      searchQuery: '',
      searching: false,
      searchMarker: null,
      lastSearchTime: 0,
      searchCache: {},
      locationLabel: '',
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN || ''
    };
  },
  computed: {
    mapCenter() {
      return [
        this.latInternal !== null ? this.latInternal : this.defaultCenter[0],
        this.lngInternal !== null ? this.lngInternal : this.defaultCenter[1]
      ];
    },
    boundsUS() {
      // Rough continental US bounds
      return [
        [24.396308, -125.0],
        [49.384358, -66.93457]
      ];
    },
    latDisplay() {
      const val = this.latInternal !== null ? this.latInternal : this.mapCenter[0];
      return val !== null ? Number(val).toFixed(4) : '—';
    },
    lngDisplay() {
      const val = this.lngInternal !== null ? this.lngInternal : this.mapCenter[1];
      return val !== null ? Number(val).toFixed(4) : '—';
    },
    radiusMilesDisplay() {
      const val = this.radiusMilesInternal !== null ? this.radiusMilesInternal : this.defaultRadius;
      const numeric = Number(val);
      return isNaN(numeric) ? this.defaultRadius : Number(numeric.toFixed(1));
    }
  },
  watch: {
    lat(val) {
      if (val !== null && this.circle) {
        this.latInternal = val;
        this.setPosition({ lat: val, lng: this.lng !== null ? this.lng : this.mapCenter[1] }, false, false);
      }
    },
    lng(val) {
      if (val !== null && this.circle) {
        this.lngInternal = val;
        this.setPosition({ lat: this.lat !== null ? this.lat : this.mapCenter[0], lng: val }, false, false);
      }
    },
    radiusMiles(val) {
      const next = this.clampRadiusMiles(val);
      if (next !== this.radiusMilesInternal) {
        this.radiusMilesInternal = next;
        this.updateRadius(next);
      }
    },
    locationName(val) {
      this.locationLabel = val || '';
    }
  },
  mounted() {
    this.radiusMilesInternal = this.clampRadiusMiles(this.radiusMiles || this.defaultRadius);
    this.latInternal = this.lat !== null ? this.lat : this.defaultCenter[0];
    this.lngInternal = this.lng !== null ? this.lng : this.defaultCenter[1];
    this.locationLabel = this.locationName || '';
    this.ensureLeaflet();
  },
  beforeDestroy() {
    window.removeEventListener('leaflet-loaded', this.onLeafletLoaded);
    window.removeEventListener('leaflet-error', this.onLeafletError);
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    refreshMap() {
      if (this.map && typeof this.map.invalidateSize === 'function') {
        this.map.invalidateSize();
      }
    },
    ensureLeaflet() {
      if (window.L) {
        this.initMap();
        return;
      }
      window.addEventListener('leaflet-loaded', this.onLeafletLoaded);
      window.addEventListener('leaflet-error', this.onLeafletError);
    },
    onLeafletLoaded() {
      this.initMap();
    },
    onLeafletError() {
      // Fallback to a static state if Leaflet cannot load
      this.leafletReady = false;
    },
    initMap() {
      if (this.leafletReady || !window.L) return;
      this.leafletReady = true;
      const L = window.L;
      const startCenter = this.mapCenter;

      this.map = L.map(this.$refs.map, {
        zoomControl: true,
        maxBounds: this.boundsUS,
        maxBoundsViscosity: 0.75
      }).setView(startCenter, 4);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 14
      }).addTo(this.map);

      this.drawnItems = new L.FeatureGroup().addTo(this.map);

      this.circle = L.circle(startCenter, {
        radius: this.radiusMeters(this.radiusMilesInternal),
        color: '#1d4ed8',
        fillColor: '#1d4ed8',
        fillOpacity: 0.15
      });
      this.circle.addTo(this.drawnItems);

      // Only enable map click if not readonly
      if (!this.readonly) {
        this.map.on('click', (e) => this.onMapClick(e));
      }

      // Enable direct circle edge dragging via Leaflet Draw (only if not readonly)
      if (!this.readonly && this.circle.editing && this.circle.editing.enable) {
        this.circle.editing.enable();
        this.circle.on('edit', (e) => this.onCircleEdited(e));
      }

      // Keep the view inside the US and focused on the selection
      this.map.fitBounds(this.boundsUS, { padding: [20, 20] });
      // Emit initial values so parent gets defaults
      this.emitLatLng(startCenter[0], startCenter[1]);
      this.emitRadiusMiles(this.radiusMilesInternal);
    },
    async handleSearch() {
      if (this.searching) return;
      const query = (this.searchQuery || '').trim();
      if (!query) return;

      if (!this.map || !window.L) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Map is not ready. Please wait a moment and try again.
            </p>
          `,
          duration: 2500,
          dangerouslyUseHTMLString: true
        });
        return;
      }

      if (!this.mapboxAccessToken) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Map search unavailable. Please set mapbox token.
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
        return;
      }

      const now = Date.now();
      if (now - this.lastSearchTime < 1000) return;
      this.lastSearchTime = now;

      // Serve from cache if available
      const cached = this.searchCache[query];
      if (cached) {
        this.applySearchResult(cached.lat, cached.lng, cached.display_name);
        return;
      }

      this.searching = true;

      try {
        const latLngMatch = query.match(/^(-?\d+\.?\d*)[\s,]+(-?\d+\.?\d*)$/);
        if (latLngMatch) {
          const lat = parseFloat(latLngMatch[1]);
          const lng = parseFloat(latLngMatch[2]);
          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            Notification({
              message: `
                <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                  <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                  <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
                </p>
                <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                  Coordinates are invalid. Use latitude, longitude.
                </p>
              `,
              duration: 2500,
              dangerouslyUseHTMLString: true
            });
            return;
          }
          this.applySearchResult(lat, lng, `${lat}, ${lng}`);
          return;
        }

        if (query.length < 3) {
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Warning</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Enter at least 3 characters to search.
              </p>
            `,
            duration: 2500,
            dangerouslyUseHTMLString: true
          });
          return;
        }

        const mapboxUrl = `https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(query)}&limit=1&access_token=${this.mapboxAccessToken}`;
        const response = await fetch(mapboxUrl);

        if (!response.ok) {
          let reason = 'Failed to search location. Please try again.';
          if (response.status === 401) reason = 'Mapbox token not authorized.';
          else if (response.status === 403) reason = 'Mapbox request forbidden.';
          else if (response.status === 429) reason = 'Mapbox rate limit reached. Please wait.';
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                ${reason}
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
          return;
        }

        const data = await response.json();
        const feature = data && data.features && data.features[0];
        const coords = feature && feature.geometry && feature.geometry.coordinates;
        if (!coords || coords.length < 2) {
          Notification({
            message: `
              <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
                <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
                <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Warning</span>
              </p>
              <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
                Location not found. Please try a different search term.
              </p>
            `,
            duration: 3000,
            dangerouslyUseHTMLString: true
          });
          return;
        }

        const lng = parseFloat(coords[0]);
        const lat = parseFloat(coords[1]);
        const displayName = (feature.properties && feature.properties.name) || feature.place_name || query;

        this.searchCache[query] = { lat, lng, display_name: displayName };
        this.applySearchResult(lat, lng, displayName);
      } catch (error) {
        console.error('Geo search failed', error);
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-error" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Error</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Unable to search location. Please try again.
            </p>
          `,
          duration: 3000,
          dangerouslyUseHTMLString: true
        });
      } finally {
        this.searching = false;
      }
    },
    applySearchResult(lat, lng, label = '') {
      if (!this.map || !window.L) return;

      // Respect bounds; if outside, warn and ignore
      if (!this.isWithinBounds({ lat, lng })) {
        Notification({
          message: `
            <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
              <i class="el-notification__icon el-icon-warning" style="margin-right: 10px;"></i> 
              <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Warning</span>
            </p>
            <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
              Location is outside the allowed area (US).
            </p>
          `,
          duration: 2500,
          dangerouslyUseHTMLString: true
        });
        return;
      }

      const L = window.L;
      this.map.setView([lat, lng], 11);
      this.setPosition({ lat, lng }, true, true);

      if (this.searchMarker) {
        this.map.removeLayer(this.searchMarker);
      }
      this.searchMarker = L.marker([lat, lng], {
        icon: L.divIcon({
          className: 'search-marker-pin',
          html: '<div class="search-marker-dot"></div>',
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        })
      }).addTo(this.map);

      this.locationLabel = label || '';
      this.emitLocationName();

      Notification({
        message: `
          <p style="display: flex; align-items: center; color: #000; font-size: 14px; font-weight: bold;">
            <i class="el-notification__icon el-icon-success" style="margin-right: 10px;"></i> 
            <span style="font-size: 16px; font-family: 'Poppins', sans-serif;">Success</span>
          </p>
          <p style="color: #000; font-size: 14px; margin-top: 10px; font-family: 'Poppins', sans-serif;">
            ${label ? `Found: ${label}` : 'Location selected.'}
          </p>
        `,
        duration: 2000,
        dangerouslyUseHTMLString: true
      });
    },
    clearSearch() {
      this.searchQuery = '';
      if (this.searchMarker && this.map) {
        this.map.removeLayer(this.searchMarker);
        this.searchMarker = null;
      }
    },
    radiusMeters(miles) {
      return (this.clampRadiusMiles(miles) || this.defaultRadius || 1) * 1609.34;
    },
    onMapClick(event) {
      if (this.readonly) return;
      this.setPosition(event.latlng, true, true);
      this.locationLabel = '';
      this.emitLocationName();
    },
    onCircleEdited(event) {
      if (!event) return;
      // Leaflet Draw emits edited layers via event.layers
      if (event.layers && event.layers.eachLayer) {
        event.layers.eachLayer(layer => this.handleCircleEditLayer(layer));
        return;
      }
      if (event.target) {
        this.handleCircleEditLayer(event.target);
      }
    },
    handleCircleEditLayer(layer) {
      if (!layer || typeof layer.getRadius !== 'function') return;
      const meters = layer.getRadius();
      const milesRaw = Number((meters / 1609.34).toFixed(2));
      const miles = this.clampRadiusMiles(milesRaw);
      this.radiusMilesInternal = miles;
      this.emitRadiusMiles(miles);
      const { lat, lng } = layer.getLatLng();
      const centerChanged = this.latInternal !== lat || this.lngInternal !== lng;
      this.latInternal = lat;
      this.lngInternal = lng;
      this.emitLatLng(lat, lng);
      if (centerChanged) {
        this.locationLabel = '';
        this.emitLocationName();
      }
      this.refreshCircleEditingHandles();
    },
    setPosition(latlng, panToCenter = true, shouldEmit = true) {
      if (!latlng) return;
      if (!this.isWithinBounds(latlng)) return;
      const { lat, lng } = latlng;
      const current = this.circle ? this.circle.getLatLng() : null;
      const samePoint = current && current.lat === lat && current.lng === lng;
      this.latInternal = lat;
      this.lngInternal = lng;
      this.suppressEmit = !shouldEmit;
      if (this.circle) {
        this.circle.setLatLng(latlng);
        this.circle.redraw();
        this.refreshCircleEditingHandles();
      }
      if (panToCenter && this.map && !samePoint) this.map.panTo(latlng);
      if (shouldEmit) {
        this.emitLatLng(lat, lng);
      }
      this.$nextTick(() => {
        this.suppressEmit = false;
      });
    },
    emitLatLng(lat, lng) {
      if (this.suppressEmit) return;
      this.$emit('update:lat', lat);
      this.$emit('update:lng', lng);
    },
    emitRadiusMiles(val) {
        if (this.suppressEmit) return;
        const next = this.clampRadiusMiles(val);
        this.$emit('update:radiusMiles', next);
    },
    emitLocationName() {
      if (this.suppressEmit) return;
      this.$emit('update:locationName', this.locationLabel || '');
    },
    onRadiusInput(val) {
      const next = this.clampRadiusMiles(val);
      this.updateRadius(next);
      this.emitRadiusMiles(next);
    },
    updateRadius(val) {
      const next = this.clampRadiusMiles(val);
      this.radiusMilesInternal = next;
      if (this.circle) {
        this.circle.setRadius(this.radiusMeters(next));
        this.refreshCircleEditingHandles();
      }
    },
    clampRadiusMiles(val) {
      const numeric = Number(val);
      const base = !isNaN(numeric) && numeric > 0 ? numeric : this.defaultRadius || 1;
      return base;
    },
    isWithinBounds(latlng) {
      if (!latlng) return false;
      if (!window.L) return true;
      const bounds = window.L.latLngBounds(this.boundsUS);
      return bounds.contains(latlng);
    },
    refreshCircleEditingHandles() {
      if (this.readonly) return;
      if (!this.circle || !this.circle.editing || typeof this.circle.editing.enable !== 'function') return;
      const enabled = typeof this.circle.editing.enabled === 'function' ? this.circle.editing.enabled() : this.circle.editing._enabled;
      if (enabled) {
        this.circle.editing.disable();
        this.circle.editing.enable();
      }
    }
  }
};
</script>

<style scoped>
.geo-radius-selector {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  margin-top: 12px;
}

.geo-radius-search {
  width: 100%;
  padding: 10px 12px 8px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.geo-radius-map {
  width: 100%;
  min-height: 500px;
  border-radius: 0;
  border: none;
}

.geo-radius-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}

.geo-radius-meta {
  font-size: 12px;
  color: #4b5563;
}

.geo-radius-search :deep(.el-input__prefix) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.geo-radius-search :deep(.el-input__icon) {
  line-height: 1;
}

.search-marker-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #409eff;
  border: 3px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
}

/* Make edit handles visible even without sprite assets */
::v-deep .leaflet-div-icon.leaflet-editing-icon,
/deep/ .leaflet-div-icon.leaflet-editing-icon {
  width: 16px !important;
  height: 16px !important;
  border-radius: 50%;
  background: #1d4ed8 !important;
  border: 2px solid #ffffff !important;
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.35);
  background-image: none !important;
}

::v-deep .leaflet-edit-move,
::v-deep .leaflet-edit-resize,
/deep/ .leaflet-edit-move,
/deep/ .leaflet-edit-resize {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25) !important;
  background-image: none !important;
  background: #1d4ed8 !important;
  border: 2px solid #ffffff !important;
  width: 16px !important;
  height: 16px !important;
  border-radius: 50%;
  opacity: 0.98;
  cursor: grab;
}

::v-deep .leaflet-edit-move.leaflet-drag-target,
::v-deep .leaflet-edit-resize.leaflet-drag-target,
/deep/ .leaflet-edit-move.leaflet-drag-target,
/deep/ .leaflet-edit-resize.leaflet-drag-target {
  cursor: grabbing;
}
</style>
